import classNames from 'classnames';
import isArray from 'lodash/isArray';
import { useCallback, useEffect, useRef, useState } from 'react';

import CaretIcon from '../icons/Caret';
import CloseIcon from '../icons/Close';
import Button from './Button';
import CheckboxButton from './Checkbox';

import styles from '../../../styles/buttons/dropdown.module.css';

interface ToggleOption {
    value: string | number;
    label: React.ReactNode;
    [key: string]: unknown;
}

type MenuPosition = 'left' | 'right' | 'auto';
type Value = string | number | (string | number)[] | null;

interface DropdownProps {
    options: ToggleOption[];
    label?: React.ReactNode;
    value?: Value;
    menuPosition?: MenuPosition;
    closeOnSelect?: boolean;
    multiple?: boolean;
    className?: string | null;
    dropdownClassName?: string | null;
    itemsClassName?: string | null;
    itemClassName?: string | null;
    buttonClassName?: string | null;
    optionClassName?: string | null;
    onChange?: ((value: Value) => void) | null;
    onOpen?: (() => void) | null;
    onClose?: (() => void) | null;
    withClear?: boolean;
}

function Dropdown({
    options,
    label = null,
    value = null,
    menuPosition: menuPositionProp = 'auto',
    closeOnSelect = false,
    multiple = true,
    className = null,
    dropdownClassName = null,
    itemsClassName = null,
    itemClassName = null,
    buttonClassName = null,
    optionClassName = null,
    onChange = null,
    onOpen = null,
    onClose = null,
    withClear = true,
}: DropdownProps) {
    const [opened, setOpened] = useState(false);
    const [menuPosition, setMenuPosition] = useState<'left' | 'right'>(
        menuPositionProp !== 'auto' ? menuPositionProp : 'left',
    );
    const refDropdown = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        if (!refDropdown.current) return;

        const boundingRect = refDropdown.current.getBoundingClientRect();
        const isOut = boundingRect.right > window.innerWidth - 20;
        const newPosition = isOut
            ? 'right'
            : menuPositionProp !== 'auto'
              ? menuPositionProp
              : 'left';

        if (menuPosition !== newPosition) {
            setMenuPosition(newPosition);
        }
    }, [menuPosition, menuPositionProp]);

    useEffect(() => {
        if (opened) {
            updatePosition();
        }
    }, [opened, updatePosition]);

    const close = useCallback(() => {
        if (!opened) return;
        setOpened(false);
        if (onClose !== null) {
            onClose();
        }
    }, [opened, onClose]);

    const open = useCallback(() => {
        if (opened) return;
        setOpened(true);
        if (onOpen !== null) {
            onOpen();
        }
    }, [opened, onOpen]);

    const toggle = useCallback(() => {
        if (opened) {
            close();
        } else {
            open();
        }
    }, [opened, close, open]);

    const onClickButton = useCallback(() => {
        toggle();
    }, [toggle]);

    const onClearOption = useCallback(() => {
        if (onChange !== null) {
            onChange(null);
        }
        close();
    }, [onChange, close]);

    const onClickOption = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, option: ToggleOption) => {
            const valueArray = (value !== null && !isArray(value) ? [value] : value) || [];
            const typedArray: (string | number)[] = Array.isArray(valueArray)
                ? (valueArray as (string | number)[])
                : [];
            let newValue: Value;

            if (multiple) {
                if (typedArray.indexOf(option.value) !== -1) {
                    const filtered = typedArray.filter((val) => val !== option.value);
                    newValue = filtered.length === 0 ? null : filtered;
                } else {
                    newValue = [...typedArray, option.value] as (string | number)[];
                }
            } else {
                if (typedArray.indexOf(option.value) !== -1) {
                    newValue = null;
                } else {
                    newValue = option.value;
                }
            }

            if (onChange !== null) {
                onChange(newValue);
            }

            if (closeOnSelect) {
                close();
            }
        },
        [value, multiple, onChange, closeOnSelect, close],
    );

    const valueArray = value !== null && !isArray(value) ? [value] : value;
    const valueArrayLength = Array.isArray(valueArray) ? valueArray.length : 0;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.opened]: opened,
                    [styles.right]: menuPosition === 'right',
                    [className]: className !== null,
                },
            ])}
        >
            <Button
                icon={
                    opened ? (
                        <CaretIcon className={styles.caretIcon} orientation="down" />
                    ) : (
                        <CaretIcon className={styles.caretIcon} orientation="left" />
                    )
                }
                iconPosition="inline"
                className={classNames([
                    styles.button,
                    {
                        [buttonClassName]: buttonClassName !== null,
                    },
                ])}
                onClick={onClickButton}
            >
                {valueArrayLength > 0 ? `${label} (${valueArrayLength})` : label}
            </Button>
            {withClear && valueArrayLength > 0 ? (
                <Button
                    icon={<CloseIcon className={styles.closeIcon} />}
                    className={classNames([styles.closeButton])}
                    onClick={onClearOption}
                />
            ) : null}
            <div
                className={classNames([
                    styles.dropdown,
                    {
                        [dropdownClassName]: dropdownClassName !== null,
                    },
                ])}
                ref={refDropdown}
            >
                <ul
                    className={classNames([
                        styles.items,
                        {
                            [itemsClassName]: itemsClassName !== null,
                        },
                    ])}
                >
                    {options.map((it, idx) => {
                        const { value: optionValue, label: optionLabel } = it;
                        const checked =
                            value !== null && Array.isArray(valueArray)
                                ? valueArray.indexOf(optionValue) !== -1
                                : false;

                        return (
                            <li
                                className={classNames([
                                    styles.item,
                                    {
                                        [itemClassName]: itemClassName !== null,
                                    },
                                ])}
                                key={`option-${optionValue}-${idx + 1}`}
                            >
                                <CheckboxButton
                                    className={classNames([
                                        styles.button,
                                        {
                                            [optionClassName]: optionClassName !== null,
                                        },
                                    ])}
                                    iconClassName={styles.icon}
                                    labelClassName={styles.label}
                                    checked={checked}
                                    onClick={(e) => onClickOption(e, it)}
                                >
                                    {optionLabel}
                                </CheckboxButton>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

export default Dropdown;
